import { useState, useEffect } from 'react';
import './index.css';
import { LoginForm } from '@/components/LoginForm';
import { ValuationForm } from '@/components/ValuationForm';
import { ThemeProvider } from '@/components/ThemeProvider';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SignOut } from '@/components/SignOut';
import { Switch } from '@/components/ui/switch';
import { Music } from 'lucide-react';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [userAccess, setUserAccess] = useState<
    | {
        instrument: boolean;
        album: boolean;
        poster: boolean;
      }
    | undefined
  >(undefined);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [dbMode, setDbMode] = useState<'demo' | 'live'>(() => {
    return (
      (localStorage.getItem('valuation_db_mode') as 'demo' | 'live') || 'demo'
    );
  });

  // Check localStorage for credentials on mount
  useEffect(() => {
    const stored = localStorage.getItem('valuation_credentials');
    if (stored) {
      const creds = JSON.parse(stored);
      // Try to authenticate with stored credentials
      fetch('/api/auth', {
        headers: {
          Authorization: `Basic ${btoa(`${creds.username}:${creds.password}`)}`,
        },
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          } else {
            throw new Error('Authentication failed');
          }
        })
        .then((data) => {
          setCredentials(creds);
          setUserAccess(data.access);
          setIsLoggedIn(true);
        })
        .catch(() => {
          localStorage.removeItem('valuation_credentials');
          setCredentials(null);
          setIsLoggedIn(false);
        })
        .finally(() => setCheckingAuth(false));
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const handleLogin = (username: string, password: string) => {
    setCredentials({ username, password });
    setIsLoggedIn(true);
    localStorage.setItem(
      'valuation_credentials',
      JSON.stringify({ username, password }),
    );
    // Fetch user access information
    fetch('/api/auth', {
      headers: {
        Authorization: `Basic ${btoa(`${username}:${password}`)}`,
      },
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
        throw new Error('Failed to fetch user access');
      })
      .then((data) => {
        setUserAccess(data.access);
      })
      .catch((error) => {
        console.error('Error fetching user access:', error);
        // Set default access if fetch fails
        setUserAccess({
          instrument: true,
          album: true,
          poster: true,
        });
      });
  };

  const handleAuthError = () => {
    setCredentials(null);
    setUserAccess(undefined);
    setIsLoggedIn(false);
    localStorage.removeItem('valuation_credentials');
  };

  const handleDbModeChange = (mode: 'demo' | 'live') => {
    setDbMode(mode);
    localStorage.setItem('valuation_db_mode', mode);
  };

  if (checkingAuth) {
    return null; // or a loading spinner
  }

  return (
    <ThemeProvider defaultTheme='dark'>
      <div className='min-h-screen bg-background text-foreground'>
        {/* App Bar */}
        <div className='fixed left-0 top-0 flex h-16 w-full items-center justify-between overflow-x-auto whitespace-nowrap bg-background px-4 shadow'>
          <span className='flex items-center text-lg font-bold'>
            <Music className='mr-2 h-6 w-6' />
            Valuation and Inventory
          </span>
          <div className='flex items-center space-x-4'>
            {isLoggedIn && (
              <div className='flex items-center px-3'>
                <span className='text-sm font-medium'>Live</span>
                <Switch
                  className='mx-2'
                  checked={dbMode === 'demo'}
                  onCheckedChange={(checked) =>
                    handleDbModeChange(checked ? 'demo' : 'live')
                  }
                />
                <span className='text-sm font-medium'>Demo</span>
              </div>
            )}
            <ThemeToggle />
            {isLoggedIn && <SignOut onSignOut={handleAuthError} />}
          </div>
        </div>
        <main className='container mx-auto mt-20 py-6'>
          {!isLoggedIn ? (
            <LoginForm onLogin={handleLogin} />
          ) : (
            <ValuationForm
              key={dbMode}
              credentials={credentials!}
              onAuthError={handleAuthError}
              dbMode={dbMode}
              userAccess={userAccess}
            />
          )}
        </main>
      </div>
    </ThemeProvider>
  );
}

export default App;
